import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { WATCHLIST } from '@/constants/testIds';
import { toast } from 'sonner';
import { AlertTriangle, Bell, Globe, Mail, Radar, RefreshCw, Server, Trash2 } from 'lucide-react';

const KIND_META = {
  domain: { icon: Globe,  label: 'DOMAIN',  placeholder: 'example.com' },
  email:  { icon: Mail,   label: 'EMAIL',   placeholder: 'operator@example.com' },
  ip:     { icon: Server, label: 'IP',      placeholder: '8.8.8.8' },
};

const STATUS_STYLES = {
  ok:      { cls: 'c-badge c-badge--active', label: 'OK' },
  changed: { cls: 'c-badge c-badge--warn',   label: 'CHANGED' },
  queued:  { cls: 'c-badge',                 label: 'QUEUED' },
  error:   { cls: 'c-badge c-badge--danger', label: 'ERROR' },
};

const formatDT = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toISOString().slice(0, 16).replace('T', ' ');
  } catch {
    return iso;
  }
};

const ALERT_KIND_LABEL = {
  first_check: 'BASELINE',
  dns_change: 'DNS_CHANGE',
  whois_change: 'WHOIS_CHANGE',
  http_change: 'HTTP_CHANGE',
  asn_change: 'ASN_CHANGE',
};

export const WatchlistPage = () => {
  const { user } = useAuth();
  const isPro = user?.subscription_status === 'active' || user?.subscription_status === 'trialing';

  const [entities, setEntities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('domain');
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!isPro) { setLoading(false); return; }
    setLoading(true);
    try {
      const [e, a] = await Promise.all([
        api.get('/watchlist'),
        api.get('/watchlist/alerts', { params: { limit: 30 } }),
      ]);
      setEntities(e.data);
      setAlerts(a.data);
    } catch {
      toast.error('Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  }, [isPro]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    try {
      const { data: created } = await api.post('/watchlist', { kind, value: value.trim(), label: label.trim() || null });
      setValue(''); setLabel('');
      toast.success('ENTITY_QUEUED');
      // Poll for baseline snapshot (backend runs first_check asynchronously)
      const started = Date.now();
      const poll = async () => {
        await load();
        // stop once entity has a checked-at or after ~8s
        try {
          const { data: fresh } = await api.get('/watchlist');
          const row = fresh.find((r) => r.id === created.id);
          if (row?.last_checked_at || Date.now() - started > 8000) return;
        } catch {
          return;
        }
        setTimeout(poll, 1200);
      };
      setTimeout(poll, 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Add failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/watchlist/${id}`);
      toast.success('ENTITY_REMOVED');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const forceCheck = async (id) => {
    try {
      await api.post(`/watchlist/${id}/check`);
      toast.success('RE_CHECKED');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Check failed');
    }
  };

  const markSeen = async () => {
    try {
      await api.post('/watchlist/alerts/mark-seen');
      toast.success('ALERTS_MARKED_SEEN');
      load();
    } catch {
      toast.error('Failed to mark seen');
    }
  };

  if (!isPro) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-8 pt-16 pb-24">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#38BDF8] mb-3">
          // ACCESS_GATE
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tighter mb-6">
          Entity Watchlist<span className="caelix-caret"></span>
        </h1>
        <div data-testid={WATCHLIST.upgradeGate} className="c-card">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" strokeWidth={2.5} />
            <div>
              <div className="text-white text-sm uppercase tracking-widest">PRO_TIER_REQUIRED</div>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                Continuously monitor domains, IPs, and emails. Caelix runs periodic
                DNS/WHOIS/reverse-DNS/ASN checks and alerts you on material changes —
                a passive early-warning net for your operator perimeter.
              </p>
            </div>
          </div>
          <Link to="/pricing" className="c-btn-primary text-xs">UPGRADE_CLEARANCE</Link>
        </div>
      </div>
    );
  }

  const unseenCount = alerts.filter((a) => !a.seen).length;

  return (
    <div data-testid={WATCHLIST.container} className="max-w-6xl mx-auto px-6 md:px-8 pt-10 pb-24">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#38BDF8] mb-3">
        // ENTITY_WATCHLIST
      </div>
      <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tighter mb-6">
        Watchlist<span className="caelix-caret"></span>
      </h1>
      <p className="text-xs text-gray-500 mb-6 max-w-2xl">
        &gt; Add domains, emails, or IPs to monitor. Caelix runs DNS/WHOIS/reverse-DNS
        checks on a 30-minute cadence and drops an alert when something changes.
      </p>

      {/* Add form */}
      <form data-testid={WATCHLIST.addForm} onSubmit={add} className="c-card mb-8">
        <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="c-label" htmlFor="watchlist-kind">KIND</label>
            <select
              id="watchlist-kind"
              data-testid={WATCHLIST.addKind}
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="c-input"
            >
              {Object.entries(KIND_META).map(([k, m]) => (
                <option key={k} value={k}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="c-label" htmlFor="watchlist-value">VALUE</label>
            <input
              id="watchlist-value"
              data-testid={WATCHLIST.addValue}
              type="text"
              required
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={KIND_META[kind].placeholder}
              className="c-input"
            />
          </div>
          <div>
            <label className="c-label" htmlFor="watchlist-label">LABEL (OPTIONAL)</label>
            <input
              id="watchlist-label"
              data-testid={WATCHLIST.addLabel}
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="internal note"
              className="c-input"
              maxLength={64}
            />
          </div>
          <button
            data-testid={WATCHLIST.addSubmit}
            type="submit"
            disabled={busy}
            className="c-btn-primary text-xs h-[42px]"
          >
            {busy ? 'ADDING…' : 'ADD_ENTITY'}
          </button>
        </div>
      </form>

      {/* Alerts */}
      <div
        data-testid={WATCHLIST.alertsPanel}
        className="c-card mb-8"
      >
        <div className="border-b border-[#3F3F46] pb-3 mb-4 flex items-center justify-between">
          <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500 flex items-center gap-2">
            <Bell className="w-3 h-3 text-[#38BDF8]" strokeWidth={2.5} />
            // ALERTS_STREAM
          </div>
          <div className="flex items-center gap-3">
            <span className={`c-badge ${unseenCount > 0 ? 'c-badge--warn' : ''}`}>
              {unseenCount} UNSEEN
            </span>
            <button
              data-testid={WATCHLIST.markSeenBtn}
              onClick={markSeen}
              disabled={unseenCount === 0}
              className="c-btn-secondary text-[10px] px-3 py-1.5"
            >
              MARK_ALL_SEEN
            </button>
          </div>
        </div>
        {alerts.length === 0 ? (
          <div className="text-xs text-gray-600 tracking-widest uppercase">&gt; no alerts yet · add an entity to receive baseline snapshots</div>
        ) : (
          <div className="max-h-72 overflow-auto">
            {alerts.map((a) => {
              const kMeta = KIND_META[a.entity_kind] || KIND_META.domain;
              const KIcon = kMeta.icon;
              return (
                <div
                  key={a.id}
                  data-testid={WATCHLIST.alertItem}
                  className={`grid grid-cols-[16px_1fr_140px_120px_120px] items-center gap-3 py-2 border-b border-[#27272A] last:border-0 text-xs ${a.seen ? 'opacity-60' : ''}`}
                >
                  <KIcon className="w-3 h-3 text-[#38BDF8]" strokeWidth={2.5} />
                  <div className="text-white font-mono truncate">{a.summary}</div>
                  <div className="text-gray-500 font-mono truncate">{a.entity_value}</div>
                  <span className={`c-badge ${a.kind === 'first_check' ? '' : 'c-badge--warn'}`}>
                    {ALERT_KIND_LABEL[a.kind] || a.kind.toUpperCase()}
                  </span>
                  <div className="text-gray-600 font-mono text-[10px] text-right">
                    {formatDT(a.created_at)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Entities table */}
      {loading ? (
        <div className="c-card text-xs text-gray-500 tracking-widest uppercase">
          LOADING<span className="caelix-dots"></span>
        </div>
      ) : entities.length === 0 ? (
        <div data-testid={WATCHLIST.emptyState} className="c-card">
          <div className="flex items-center gap-3">
            <Radar className="w-4 h-4 text-[#38BDF8]" strokeWidth={2.5} />
            <div className="text-xs text-gray-500 tracking-widest uppercase">
              READY · add your first entity above to start monitoring
            </div>
          </div>
        </div>
      ) : (
        <div className="c-card">
          <div className="border-b border-[#3F3F46] pb-3 mb-4 text-[10px] tracking-[0.3em] uppercase text-gray-500">
            // WATCHED_ENTITIES · {entities.length}/25
          </div>
          <div className="grid grid-cols-[24px_1fr_1fr_120px_150px_120px_100px] gap-3 items-center text-[10px] tracking-[0.2em] uppercase text-gray-500 py-2 border-b border-[#3F3F46]">
            <div></div>
            <div>VALUE</div>
            <div>LABEL</div>
            <div>STATUS</div>
            <div>LAST_CHECK</div>
            <div>ALERTS</div>
            <div></div>
          </div>
          {entities.map((e) => {
            const KIcon = (KIND_META[e.kind] || KIND_META.domain).icon;
            const st = STATUS_STYLES[e.status] || STATUS_STYLES.queued;
            return (
              <div
                key={e.id}
                data-testid={WATCHLIST.entityRow}
                className="grid grid-cols-[24px_1fr_1fr_120px_150px_120px_100px] gap-3 items-center py-3 border-b border-[#27272A] last:border-0 text-xs hover:bg-[#27272A] transition-colors"
              >
                <KIcon className="w-4 h-4 text-[#38BDF8]" strokeWidth={2.5} />
                <div className="text-white font-mono truncate">{e.value}</div>
                <div className="text-gray-500 font-mono truncate">{e.label || '—'}</div>
                <span className={st.cls}>{st.label}</span>
                <div className="text-gray-500 font-mono text-[10px]">{formatDT(e.last_checked_at)}</div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-white">{e.alerts_count}</span>
                  {e.unseen_alerts > 0 && (
                    <span className="c-badge c-badge--warn">{e.unseen_alerts} NEW</span>
                  )}
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    data-testid={WATCHLIST.entityCheck}
                    onClick={() => forceCheck(e.id)}
                    className="c-btn-secondary text-[10px] px-2 py-1 inline-flex items-center gap-1"
                    title="Force re-check"
                  >
                    <RefreshCw className="w-3 h-3" strokeWidth={2.5} />
                  </button>
                  <button
                    data-testid={WATCHLIST.entityDelete}
                    onClick={() => remove(e.id)}
                    className="c-btn-danger text-[10px] px-2 py-1"
                    title="Remove"
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WatchlistPage;
